import { useEffect, useRef, useState } from 'react';
import { confirm } from '../utils/confirm';
import { useToast } from '../components/Toast';
import { fmtVND, fmtNum, fmtDateTime } from '../utils/format';
import {
  listPurchaseReports,
  createPurchaseReport,
  updatePurchaseReport,
  removePurchaseReport,
  ensureMonthPurchaseReports,
  getPurchaseMonthNote,
  savePurchaseMonthNote,
  getPurchaseWeekStatus,
  submitPurchaseWeek,
} from '../api/purchaseReports';
import { summaryTotalOrders, monthlyTotalOrders } from '../api/totalOrders';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';
import { getScanStatus } from '../api/socson';

const thisMonth = () => new Date().toISOString().slice(0, 7);
const today = () => new Date().toISOString().slice(0, 10);
// Ngày theo giờ VN (UTC+7) để khớp khóa summary từ backend.
const ymd = (d) => new Date(new Date(d).getTime() + 7 * 3600 * 1000).toISOString().slice(0, 10);
const fmtDay = (d) => {
  const x = new Date(d);
  return `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}`;
};
const WD = ['CN', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
const weekday = (d) => WD[new Date(d).getDay()];
const fmtDayShort = (d) => {
  const x = new Date(d);
  return `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}`;
};
// Nguồn trong báo cáo -> phân loại đơn (để TỰ ĐỘNG đếm "Tổng đơn" từ danh sách quét).
// Nguồn không có trong map (Mercari comment, Đơn bid/Offer Ebay) -> nhập tay.
const SOURCE_TO_CATEGORY = {
  'Yahoo Auction': 'Yahoo Auction',
  Mercari: 'Mercari',
  Ebay: 'Ebay',
  'Site ngoài US': 'Site ngoài US',
  'Site Ngoài': 'Site khác',
};
// Tỷ lệ xử lý = đã xử lý / tổng đơn (%). Null nếu chưa có đơn.
const rateOf = (t, p) => (t > 0 ? (p / t) * 100 : null);
const fmtRate = (r) => (r == null ? '—' : `${r.toFixed(2).replace('.', ',')}%`);
const rateCls = (r) => (r == null ? '' : r >= 95 ? 'ok' : r >= 80 ? 'warn' : 'bad');

// Biểu đồ đường: số đơn theo ngày cho nhiều nguồn. Bấm vào điểm để xem chi tiết.
function MonthLineChart({ labels, series }) {
  const [sel, setSel] = useState(null); // chỉ số điểm đang chọn
  const W = 1000;
  const H = 220;
  const padL = 44;
  const padR = 16;
  const padT = 12;
  const padB = 24;
  const n = labels.length;
  const max = Math.max(1, ...series.flatMap((s) => s.data));
  const x = (i) => (n <= 1 ? (W - padL - padR) / 2 + padL : padL + (i * (W - padL - padR)) / (n - 1));
  const y = (v) => padT + (1 - v / max) * (H - padT - padB);
  const grid = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const step = Math.max(1, Math.ceil(n / 14));

  // Hộp chi tiết khi chọn 1 điểm
  let tip = null;
  if (sel != null) {
    const bw = 150;
    const lines = series.length + 1;
    const bh = 16 + lines * 16;
    let bx = x(sel) + 10;
    if (bx + bw > W) bx = x(sel) - bw - 10;
    const by = Math.max(2, padT);
    tip = (
      <g pointerEvents="none">
        <line x1={x(sel)} y1={padT} x2={x(sel)} y2={H - padB} stroke="var(--muted)" strokeDasharray="3 3" strokeWidth="1" />
        <rect x={bx} y={by} width={bw} height={bh} rx="8" fill="var(--panel)" stroke="var(--border)" />
        <text x={bx + 10} y={by + 16} fontSize="12" fontWeight="700" fill="var(--text)">
          {labels[sel]}
        </text>
        {series.map((s, k) => (
          <g key={s.name}>
            <circle cx={bx + 14} cy={by + 30 + k * 16} r="4" fill={s.color} />
            <text x={bx + 24} y={by + 34 + k * 16} fontSize="11.5" fill="var(--text)">
              {s.name}: {s.data[sel] || 0}
            </text>
          </g>
        ))}
      </g>
    );
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} onClick={() => setSel(null)}>
      {grid.map((g, i) => {
        const yy = y(g);
        return (
          <g key={i}>
            <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke="var(--border)" strokeWidth="1" />
            <text x={padL - 6} y={yy + 4} textAnchor="end" fontSize="11" fill="var(--muted)">
              {g}
            </text>
          </g>
        );
      })}
      {labels.map((lb, i) =>
        i % step === 0 ? (
          <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="11" fill="var(--muted)">
            {lb}
          </text>
        ) : null
      )}
      {series.map((s) => (
        <g key={s.name}>
          <polyline points={s.data.map((v, i) => `${x(i)},${y(v)}`).join(' ')} fill="none" stroke={s.color} strokeWidth="2.5" />
          {s.data.map((v, i) => (
            <circle key={i} cx={x(i)} cy={y(v)} r={sel === i ? 4 : 2.5} fill={s.color} />
          ))}
        </g>
      ))}
      {/* vùng bấm rộng cho mỗi điểm (theo cột) */}
      {labels.map((lb, i) => (
        <rect
          key={'h' + i}
          x={x(i) - 8}
          y={padT}
          width={16}
          height={H - padT - padB}
          fill="transparent"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            setSel(i);
          }}
        />
      ))}
      {tip}
    </svg>
  );
}

export default function PurchaseReportPage({ embedded = false }) {
  const toast = useToast();
  const { me } = useAuth();
  // Nút "Quét đơn hàng": chỉ GĐ/CV + Trưởng phòng DVKH (phòng mua); chỉ bật sau 16:30 thứ 5 (dự phòng).
  const canScanOrders = me?.role === 'GD' || me?.role === 'CV' || (me?.deptHeadOf || []).includes('Phòng DVKH');
  const afterThu1630 = (() => {
    const d = new Date(); const w = d.getDay();
    const late = d.getHours() > 16 || (d.getHours() === 16 && d.getMinutes() >= 30);
    return (w === 4 && late) || w === 5 || w === 6 || w === 0;
  })();
  const [scanningOrders, setScanningOrders] = useState(false);
  const [month, setMonth] = useState(thisMonth());
  const [days, setDays] = useState([]);
  const [summary, setSummary] = useState({}); // { 'YYYY-MM-DD': { category: count } } — tự đếm Tổng đơn
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newDate, setNewDate] = useState(today());
  const [saveState, setSaveState] = useState('');
  const [chartTab, setChartTab] = useState('day'); // 'day' | 'month'
  const [monthly, setMonthly] = useState(null); // { months, data }
  const [note, setNote] = useState('');
  const [noteMeta, setNoteMeta] = useState({ updatedBy: '', updatedAt: null });
  const [noteSaved, setNoteSaved] = useState('');
  const [noteCanEdit, setNoteCanEdit] = useState(false); // NV Phòng DVKH: ghi thêm
  const [noteCanDelete, setNoteCanDelete] = useState(false); // TP Phòng DVKH: sửa/xóa
  const [weekSub, setWeekSub] = useState(null); // trạng thái nộp báo cáo tuần hiện tại

  const reloadWeekStatus = () => getPurchaseWeekStatus().then(setWeekSub).catch(() => {});
  useEffect(() => { reloadWeekStatus(); }, []);

  async function onSubmitWeek() {
    if (!(await confirm('Nộp báo cáo mua hàng của TUẦN NÀY? Sẽ hiển thị "Đã nộp" ở màn Báo cáo tuần.'))) return;
    try {
      await submitPurchaseWeek();
      toast('Đã nộp báo cáo tuần');
      reloadWeekStatus();
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi nộp báo cáo', 'danger');
    }
  }

  useEffect(() => {
    getPurchaseMonthNote(month)
      .then((d) => {
        setNote(d.content || '');
        setNoteMeta({ updatedBy: d.updatedBy, updatedAt: d.updatedAt });
        setNoteCanEdit(!!d.canEdit);
        setNoteCanDelete(!!d.canDelete);
        setNoteSaved('');
      })
      .catch(() => {});
  }, [month]);

  async function saveNote() {
    try {
      await savePurchaseMonthNote(month, note);
      setNoteSaved('Đã lưu ' + new Date().toLocaleTimeString('vi'));
    } catch (err) {
      setNoteSaved(err.response?.data?.message || 'Lỗi lưu');
    }
  }

  useEffect(() => {
    if (chartTab === 'month' && !monthly) monthlyTotalOrders().then(setMonthly).catch(() => {});
  }, [chartTab, monthly]);
  const timers = useRef({});
  const daysRef = useRef([]);
  daysRef.current = days;
  const scroller = useRef(null);
  const pendingScroll = useRef('end'); // 'start' | 'end' | null — vị trí cuộn sau khi nạp tháng
  const addMonth = (ym, delta) => {
    const [y, m] = ym.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  // Mũi tên: cuộn trong tháng; tới mép thì chuyển sang tháng trước/sau.
  const scrollTl = (dir) => {
    const el = scroller.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 5;
    const atStart = el.scrollLeft <= 5;
    if (dir === 1 && atEnd) {
      pendingScroll.current = 'start';
      setMonth(addMonth(month, 1));
      return;
    }
    if (dir === -1 && atStart) {
      pendingScroll.current = 'end';
      setMonth(addMonth(month, -1));
      return;
    }
    el.scrollBy({ left: dir * 640, behavior: 'smooth' });
  };

  const load = async (m = month) => {
    setLoading(true);
    // Tự tạo báo cáo cho mọi ngày trong tháng có đơn (admin); người xem thường thì bỏ qua.
    try {
      await ensureMonthPurchaseReports(m);
    } catch (e) {
      /* không phải admin -> bỏ qua */
    }
    try {
      // NV không xem được báo cáo nhập-tay của DVKH (403) -> vẫn hiện biểu đồ/đơn của mình.
      const [d, s] = await Promise.all([
        listPurchaseReports(m).catch(() => ({ items: [], canEdit: false })),
        summaryTotalOrders(m).catch(() => ({})),
      ]);
      setDays(d.items || []);
      setCanEdit(d.canEdit);
      setSummary(s);
    } finally {
      setLoading(false);
    }
  };
  // Quét đơn hàng cả tháng trên server (dùng token SocSon) → tự điền Báo cáo mua hàng.
  async function scanOrders() {
    setScanningOrders(true);
    try {
      await client.post('/socson/scan/purchase-month');
      toast('Đã bắt đầu quét đơn hàng ở nền…');
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const m = await getScanStatus().catch(() => null);
        if (m?.purchaseMonth && m.purchaseMonth.status !== 'running') {
          if (m.purchaseMonth.status === 'error') toast(`Lỗi quét: ${m.purchaseMonth.lastError || '?'}`, 'danger');
          else toast('Đã cập nhật Báo cáo mua hàng từ đơn SocSon.');
          await load();
          break;
        }
      }
    } catch (err) {
      toast(err.response?.data?.message || 'Lỗi khi bắt đầu quét', 'danger');
    } finally {
      setScanningOrders(false);
    }
  }
  useEffect(() => {
    load(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);


  // Sau khi nạp ngày -> cuộn theo hướng đã định (đầu tháng / cuối tháng).
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (pendingScroll.current === 'start') el.scrollLeft = 0;
    else el.scrollLeft = el.scrollWidth; // mặc định: tới ngày mới nhất / tổng tháng
    pendingScroll.current = 'end';
  }, [days]);

  // Tổng đơn của 1 dòng: tự đếm từ danh sách quét nếu nguồn có map; ngược lại lấy số nhập tay.
  // Trả về { count, money } tự tính từ đơn đã quét; null nếu nguồn nhập tay.
  const autoStat = (day, row) => {
    const cat = SOURCE_TO_CATEGORY[row.source];
    if (!cat) return null;
    return ((summary.days || {})[ymd(day.date)] || {})[cat] || { count: 0, money: 0 };
  };
  const autoTotal = (day, row) => {
    const s = autoStat(day, row);
    return s == null ? null : s.count;
  };
  const effTotal = (day, row) => {
    const a = autoTotal(day, row);
    return a == null ? Number(row.totalOrders) || 0 : a;
  };
  // Tiền theo tệ gốc: JP -> Yên (¥), US -> Đô ($).
  const fmtMoney = (amount, region) => (region === 'US' ? `$${fmtNum(amount)}` : `¥${fmtNum(amount)}`);

  // Tổng cả tháng (cộng dồn mọi ngày theo từng nguồn).
  const monthAgg = (() => {
    const rows = days[0] ? days[0].rows.map((r) => ({ source: r.source, region: r.region, count: 0, money: 0 })) : [];
    days.forEach((day) =>
      day.rows.forEach((r, ri) => {
        if (!rows[ri]) return;
        const s = autoStat(day, r);
        rows[ri].count += effTotal(day, r);
        rows[ri].money += s ? s.money : 0;
      })
    );
    const tot = rows.reduce(
      (a, r) => ({
        t: a.t + r.count,
        jp: a.jp + (r.region === 'JP' ? r.money : 0),
        us: a.us + (r.region === 'US' ? r.money : 0),
      }),
      { t: 0, jp: 0, us: 0 }
    );
    return { rows, tot };
  })();
  const monthLabel = `${Number(month.split('-')[1])}/${month.split('-')[0]}`;

  // Dữ liệu biểu đồ: số đơn theo ngày cho Yahoo / Mercari / Ebay.
  const chartDays = [...days].sort((a, b) => new Date(a.date) - new Date(b.date));
  const chartLabels = chartDays.map((d) => String(new Date(d.date).getDate()));
  const CHART_CATS = [
    ['Yahoo Auction', '#5b8cff'],
    ['Mercari', '#f59e0b'],
    ['Ebay', '#16a34a'],
  ];
  const chartSeries = CHART_CATS.map(([name, color]) => ({
    name,
    color,
    data: chartDays.map((d) => (((summary.days || {})[ymd(d.date)] || {})[name] || {}).count || 0),
  }));

  // Biểu đồ THÁNG: so sánh tổng số đơn giữa các tháng.
  const monthlyMonths = monthly?.months || [];
  const monthlyLabels = monthlyMonths.map((m) => `T${Number(m.slice(5, 7))}/${m.slice(2, 4)}`);
  const monthlySeries = CHART_CATS.map(([name, color]) => ({
    name,
    color,
    data: monthlyMonths.map((m) => (monthly.data[m] || {})[name] || 0),
  }));

  const scheduleSave = (dayId) => {
    clearTimeout(timers.current[dayId]);
    setSaveState('Đang lưu…');
    timers.current[dayId] = setTimeout(async () => {
      const day = daysRef.current.find((d) => d._id === dayId);
      if (!day) return;
      try {
        await updatePurchaseReport(dayId, day.rows);
        setSaveState('Đã lưu ' + new Date().toLocaleTimeString());
      } catch (err) {
        setSaveState('⚠️ Lưu lỗi');
      }
    }, 600);
  };

  const updateCell = (dayId, ri, field, value) => {
    setDays((prev) =>
      prev.map((d) =>
        d._id === dayId ? { ...d, rows: d.rows.map((r, i) => (i === ri ? { ...r, [field]: value } : r)) } : d
      )
    );
    scheduleSave(dayId);
  };

  async function addDay() {
    try {
      await createPurchaseReport(newDate);
      toast('Đã thêm ngày báo cáo');
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function delDay(day) {
    if (!await confirm(`Xóa báo cáo ngày ${fmtDay(day.date)}?`)) return;
    try {
      await removePurchaseReport(day._id);
      toast('Đã xóa');
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  const numCell = (day, ri, field) =>
    canEdit ? (
      <input
        type="number"
        min="0"
        className={`pr-num${day.rows[ri][field] ? '' : ' blankcell'}`}
        style={{ width: '100%', maxWidth: 80, textAlign: 'center', padding: '3px 6px' }}
        value={day.rows[ri][field] || ''}
        onChange={(e) => updateCell(day._id, ri, field, Number(e.target.value) || 0)}
      />
    ) : day.rows[ri][field] ? (
      fmtNum(day.rows[ri][field])
    ) : (
      ''
    );

  return (
    <>
      {!embedded && (
        <div className="page-head">
          <div>
            <h2>Báo cáo mua hàng</h2>
            <div className="sub">Theo ngày · Tổng đơn tự đếm từ Danh sách quét · Tỷ lệ = đã xử lý ÷ tổng đơn.</div>
          </div>
        </div>
      )}

      <div className="row between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div className="row" style={{ gap: 8 }}>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }} />
          {canScanOrders && (
            <button className="btn sm ghost" disabled={scanningOrders || !afterThu1630} onClick={scanOrders}
              title={!afterThu1630 ? 'Chỉ quét thủ công sau 16:30 thứ 5 (dự phòng khi quét tự động lỗi)' : 'Quét đơn hàng cả tháng → cập nhật báo cáo'}>
              {scanningOrders ? 'Đang quét…' : '🔄 Quét đơn hàng'}
            </button>
          )}
          {canEdit && (
            <>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                style={{ width: 'auto' }}
                onClick={(e) => e.currentTarget.showPicker?.()}
              />
              <button className="btn sm" onClick={addDay}>
                + Thêm ngày
              </button>
            </>
          )}
        </div>
        <div className="row" style={{ gap: 12 }}>
          {summary.lastSyncedAt && (
            <span className="small muted">
              Cập nhật lần cuối: {fmtDateTime(summary.lastSyncedAt)}
            </span>
          )}
          {canEdit && <span className="small muted">{saveState || 'Tự lưu khi sửa'}</span>}
        </div>
      </div>

      {!loading && (
        <div className="card">
          <div className="row between" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div className="seg">
              <button className={chartTab === 'day' ? 'active' : ''} onClick={() => setChartTab('day')}>
                📈 Biểu đồ ngày
              </button>
              <button className={chartTab === 'month' ? 'active' : ''} onClick={() => setChartTab('month')}>
                📊 Biểu đồ tháng
              </button>
            </div>
            <div className="row" style={{ gap: 14 }}>
              {CHART_CATS.map(([name, color]) => (
                <span key={name} className="small" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: color, display: 'inline-block' }} />
                  {name}
                </span>
              ))}
            </div>
          </div>
          {chartTab === 'day' ? (
            days.length > 0 ? (
              <>
                <div className="small muted" style={{ marginBottom: 6 }}>Số đơn theo ngày — tháng {monthLabel} (so sánh giữa các ngày)</div>
                <MonthLineChart labels={chartLabels} series={chartSeries} />
              </>
            ) : (
              <div className="empty">Chưa có dữ liệu ngày trong tháng này.</div>
            )
          ) : !monthly ? (
            <div className="empty">Đang tải…</div>
          ) : monthlyMonths.length ? (
            <>
              <div className="small muted" style={{ marginBottom: 6 }}>Tổng số đơn theo tháng (so sánh giữa các tháng)</div>
              <MonthLineChart labels={monthlyLabels} series={monthlySeries} />
            </>
          ) : (
            <div className="empty">Chưa có dữ liệu tháng.</div>
          )}
        </div>
      )}

      {loading ? (
        <div className="card">
          <div className="empty">Đang tải...</div>
        </div>
      ) : (
        <div className="tl-wrap">
          <button className="tl-arrow" onClick={() => scrollTl(-1)} title="Ngày trước / tháng trước">
            ‹
          </button>
          <div className="tl-scroll" ref={scroller}>
            {!days.length && (
              <div
                className="prtl-cell"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', minHeight: 120 }}
              >
                Chưa có báo cáo trong tháng này
              </div>
            )}
            {[...days].sort((a, b) => new Date(a.date) - new Date(b.date)).map((day) => {
              const tot = day.rows.reduce(
                (a, r) => {
                  const s = autoStat(day, r);
                  const money = s ? s.money : 0;
                  return {
                    t: a.t + effTotal(day, r),
                    jp: a.jp + (r.region === 'JP' ? money : 0),
                    us: a.us + (r.region === 'US' ? money : 0),
                  };
                },
                { t: 0, jp: 0, us: 0 }
              );
              return (
                <div className="prtl-cell" key={day._id}>
                  <div className="prtl-head">
                    {weekday(day.date)} · {fmtDayShort(day.date)}
                  </div>
                  <div className="tbl-wrap">
                  <table className="prtl-tbl">
                    <tbody>
                      {day.rows.map((r, ri) => {
                        const stat = autoStat(day, r); // null = nguồn nhập tay
                        return (
                          <tr key={ri}>
                            <td className="prtl-src">{r.source}</td>
                            <td className="prtl-cnt">
                              {stat == null ? numCell(day, ri, 'totalOrders') : stat.count ? fmtNum(stat.count) : ''}
                            </td>
                            <td className="prtl-money">{stat && stat.money ? fmtMoney(stat.money, r.region) : ''}</td>
                          </tr>
                        );
                      })}
                      <tr className="prtl-tot">
                        <td>Tổng</td>
                        <td className="prtl-cnt">{fmtNum(tot.t)}</td>
                        <td className="prtl-money">
                          {tot.jp ? <div>{fmtMoney(tot.jp, 'JP')}</div> : null}
                          {tot.us ? <div>{fmtMoney(tot.us, 'US')}</div> : null}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                </div>
              );
            })}

            {/* Ô tổng cả tháng (cuối timeline) */}
            {days.length > 0 && (
            <div className="prtl-cell prtl-month">
              <div className="prtl-head">📊 Tổng tháng {monthLabel}</div>
              <div className="tbl-wrap">
              <table className="prtl-tbl">
                <tbody>
                  {monthAgg.rows.map((r, i) => (
                    <tr key={i}>
                      <td className="prtl-src">{r.source}</td>
                      <td className="prtl-cnt">{r.count ? fmtNum(r.count) : ''}</td>
                      <td className="prtl-money">{r.money ? fmtMoney(r.money, r.region) : ''}</td>
                    </tr>
                  ))}
                  <tr className="prtl-tot">
                    <td>Tổng</td>
                    <td className="prtl-cnt">{fmtNum(monthAgg.tot.t)}</td>
                    <td className="prtl-money">
                      {monthAgg.tot.jp ? <div>{fmtMoney(monthAgg.tot.jp, 'JP')}</div> : null}
                      {monthAgg.tot.us ? <div>{fmtMoney(monthAgg.tot.us, 'US')}</div> : null}
                    </td>
                  </tr>
                </tbody>
              </table>
              </div>
              {weekSub && weekSub.canEdit && (
                <div style={{ marginTop: 8 }}>
                  {weekSub.submitted && (
                    <div className="small" style={{ color: '#16a34a', textAlign: 'center', marginBottom: 4 }}>
                      ✓ Đã nộp tuần này{weekSub.submittedAt ? ` · ${fmtDateTime(weekSub.submittedAt)}` : ''}
                    </div>
                  )}
                  <button className="btn sm" style={{ width: '100%' }} onClick={onSubmitWeek}>
                    {weekSub.submitted ? '↻ Nộp lại báo cáo tuần' : '📤 Nộp báo cáo tuần'}
                  </button>
                </div>
              )}
            </div>
            )}
          </div>
          <button className="tl-arrow" onClick={() => scrollTl(1)} title="Ngày sau / tháng sau">
            ›
          </button>
        </div>
      )}

      {!loading && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="row between" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0 }}>📝 Ghi chú phòng mua — tháng {monthLabel}</h3>
            <span className="small muted">
              {noteSaved || (noteMeta.updatedBy ? `Cập nhật bởi ${noteMeta.updatedBy}` : '')}
            </span>
          </div>
          <div className="small muted" style={{ marginBottom: 8 }}>Công việc phát sinh, vấn đề cần giải quyết trong tháng…</div>
          {noteCanEdit ? (
            <>
              {!noteCanDelete && (
                <div className="small" style={{ color: 'var(--warn)', marginBottom: 6 }}>
                  Bạn chỉ được <b>ghi thêm</b> — không xóa/sửa nội dung cũ (chỉ trưởng phòng mới sửa/xóa được).
                </div>
              )}
              <textarea
                rows={6}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onBlur={saveNote}
                placeholder="Ghi các việc phát sinh / vấn đề cần xử lý của phòng mua trong tháng…"
              />
              <div className="row" style={{ marginTop: 8 }}>
                <div className="spacer" />
                <button className="btn sm" onClick={saveNote}>
                  Lưu ghi chú
                </button>
              </div>
            </>
          ) : (
            <div className="note-content" style={{ whiteSpace: 'pre-wrap' }}>
              {note || <span className="small muted">Chưa có ghi chú.</span>}
            </div>
          )}
        </div>
      )}
    </>
  );
}
